import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './AdminLogs.css';

function AdminLogs() {
  const [logFiles, setLogFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalLines, setTotalLines] = useState(0);
  const logsEndRef = useRef(null);

  const LINES_PER_PAGE = 100;

  useEffect(() => {
    fetchLogFiles();
  }, []);

  useEffect(() => {
    if (selectedFile) {
      fetchLogs();
    }
  }, [selectedFile, offset]);

  useEffect(() => {
    let interval;
    if (autoRefresh && selectedFile) {
      interval = setInterval(() => {
        fetchLogs(true);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, selectedFile]);

  const fetchLogFiles = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/logs', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data || response.data.length === 0) {
        setError('No log files found on the server');
        return;
      }

      setLogFiles(response.data);

      // Auto-select the most recent combined log
      const defaultLog = response.data.find(f => f.type === 'combined');
      if (defaultLog && !selectedFile) {
        setSelectedFile(defaultLog.name);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load log files';
      setError(`Error loading logs: ${errorMsg}`);
      console.error('Log fetch error:', err);
    }
  };

  const fetchLogs = async (isRefresh = false) => {
    if (!selectedFile) return;

    try {
      if (!isRefresh) setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/admin/logs/${selectedFile}`, {
        params: { lines: LINES_PER_PAGE, offset },
        headers: { Authorization: `Bearer ${token}` }
      });

      setLogs(response.data.lines);
      setHasMore(response.data.hasMore);
      setTotalLines(response.data.totalLines);
      if (!isRefresh) setLoading(false);
    } catch (err) {
      setError('Failed to load logs');
      setLoading(false);
      console.error(err);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `/api/admin/logs/${filename}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download log file');
      console.error(err);
    }
  };

  const parseLogLine = (line) => {
    try {
      // Try to parse as JSON
      const parsed = JSON.parse(line);
      return {
        timestamp: parsed.timestamp,
        level: parsed.level,
        message: parsed.message,
        metadata: parsed,
        raw: line
      };
    } catch {
      // If not JSON, parse plain text format
      const match = line.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) \[(\w+)\]: (.+)$/);
      if (match) {
        return {
          timestamp: match[1],
          level: match[2],
          message: match[3],
          raw: line
        };
      }
      return { raw: line, level: 'info' };
    }
  };

  const filteredLogs = logs.filter(line => {
    const parsed = parseLogLine(line);
    const matchesSearch = !searchTerm ||
      line.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = levelFilter === 'all' ||
      parsed.level?.toLowerCase() === levelFilter.toLowerCase();
    return matchesSearch && matchesLevel;
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / 1048576).toFixed(2) + ' MB';
  };

  const getLogFileLabel = (file) => {
    if (file.name.includes('combined') && file.name.includes(new Date().toISOString().split('T')[0].replace(/-/g, '-'))) {
      return "Today's Activity";
    }
    if (file.name.includes('combined')) {
      return `Activity - ${file.name.match(/\d{4}-\d{2}-\d{2}/)?.[0] || ''}`;
    }
    if (file.name.includes('error') && file.name.includes(new Date().toISOString().split('T')[0].replace(/-/g, '-'))) {
      return "Today's Errors";
    }
    if (file.name.includes('error')) {
      return `Errors - ${file.name.match(/\d{4}-\d{2}-\d{2}/)?.[0] || ''}`;
    }
    if (file.name.includes('access')) {
      return 'HTTP Requests';
    }
    return file.name;
  };

  const getLogFileDescription = (file) => {
    if (file.name.includes('combined')) {
      return 'All server activity (errors, warnings, info)';
    }
    if (file.name.includes('error')) {
      return 'Errors and warnings only';
    }
    if (file.name.includes('access')) {
      return 'HTTP request logs';
    }
    if (file.name.includes('exceptions')) {
      return 'Uncaught exceptions';
    }
    if (file.name.includes('rejections')) {
      return 'Unhandled promise rejections';
    }
    return '';
  };

  // Only show relevant logs to users
  const visibleLogFiles = logFiles.filter(file => {
    // Show combined and error logs
    return file.name.includes('combined') || file.name.includes('error');
  });

  const getLevelClass = (level) => {
    switch (level?.toLowerCase()) {
      case 'error': return 'log-error';
      case 'warn': return 'log-warn';
      case 'info': return 'log-info';
      case 'http': return 'log-http';
      case 'debug': return 'log-debug';
      default: return '';
    }
  };

  return (
    <div className="admin-logs">
      {error && (
        <div className="error-banner">
          <strong>⚠️ Error:</strong> {error}
          <button onClick={() => { setError(null); fetchLogFiles(); }} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      {logFiles.length === 0 && !error ? (
        <div className="empty-logs-state">
          <h2>Loading Server Logs...</h2>
          <p>Please wait while we fetch the log files from the server.</p>
        </div>
      ) : (
      <div className="logs-layout">
        <div className="log-files-sidebar">
          <h3>Server Logs</h3>
          <p className="sidebar-help">Click a log to view its contents</p>
          {visibleLogFiles.length === 0 ? (
            <div className="no-visible-logs">
              <p>No combined or error logs found. Check the server logs directory.</p>
            </div>
          ) : (
          <div className="log-files-list">
            {visibleLogFiles.map(file => (
              <div
                key={file.name}
                className={`log-file-item ${selectedFile === file.name ? 'active' : ''}`}
                onClick={() => { setSelectedFile(file.name); setOffset(0); }}
              >
                <div className="file-info">
                  <div className="file-name">{getLogFileLabel(file)}</div>
                  <div className="file-description">{getLogFileDescription(file)}</div>
                  <div className="file-meta">
                    <span className="file-size">{formatFileSize(file.size)}</span>
                  </div>
                </div>
                <button
                  className="btn-icon"
                  onClick={(e) => { e.stopPropagation(); handleDownload(file.name); }}
                  title="Download log file"
                >
                  ⬇
                </button>
              </div>
            ))}
          </div>
          )}
        </div>

        <div className="logs-viewer">
          <div className="logs-controls">
            <div className="controls-row">
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />

              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="level-filter"
              >
                <option value="all">All Levels</option>
                <option value="error">Error</option>
                <option value="warn">Warning</option>
                <option value="info">Info</option>
                <option value="http">HTTP</option>
                <option value="debug">Debug</option>
              </select>

              <label className="auto-refresh">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                Auto-refresh
              </label>

              <button
                className="btn btn-primary"
                onClick={() => fetchLogs()}
                disabled={!selectedFile}
              >
                Refresh
              </button>
            </div>
          </div>

          {selectedFile && (
            <div className="logs-status-bar">
              <span className="status-info">
                <strong>{selectedFile}</strong> - {totalLines.toLocaleString()} total lines
              </span>
              <span className="status-info">
                Showing {filteredLogs.length} {filteredLogs.length === 1 ? 'line' : 'lines'}
              </span>
            </div>
          )}

          {loading && <div className="loading">Loading logs...</div>}

          <div className="logs-content">
            {filteredLogs.length === 0 ? (
              <div className="no-logs">No logs to display</div>
            ) : (
              filteredLogs.map((line, index) => {
                const parsed = parseLogLine(line);
                return (
                  <div key={index} className={`log-line ${getLevelClass(parsed.level)}`}>
                    <span className="log-timestamp">{parsed.timestamp}</span>
                    <span className="log-level">{parsed.level}</span>
                    <span className="log-message">{parsed.message || parsed.raw}</span>
                  </div>
                );
              })
            )}
            <div ref={logsEndRef} />
          </div>

          {(offset > 0 || hasMore) && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => setOffset(Math.max(0, offset - LINES_PER_PAGE))}
                disabled={offset === 0}
              >
                Previous
              </button>
              <span className="pagination-info">
                Showing {offset + 1} - {Math.min(offset + LINES_PER_PAGE, offset + filteredLogs.length)}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => setOffset(offset + LINES_PER_PAGE)}
                disabled={!hasMore}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

export default AdminLogs;
